import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const LINKS = [
  { to: "/#story", label: "Story", testid: "nav-link-story" },
  { to: "/catalogue", label: "Catalogue", testid: "nav-link-catalogue" },
  { to: "/#process", label: "Process", testid: "nav-link-process" },
  { to: "/contact", label: "Contact", testid: "nav-link-contact" },
  { to: "/admin", label: "Admin", testid: "nav-link-admin" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const onDark = (pathname === "/festive" || pathname.startsWith("/season")) && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="nav-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled
          ? "bg-cream/85 backdrop-blur-xl border-b border-gold/15 shadow-[0_8px_30px_rgba(28,25,23,0.05)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className={`font-serif text-2xl tracking-[0.3em] font-medium transition-colors duration-500 ${onDark ? "text-cream" : "text-ink"}`}>
          GRAT<span className="text-gold-brass">I</span>ORA
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <Link
              key={l.testid}
              to={l.to}
              data-testid={l.testid}
              className={`nav-link text-sm tracking-wide transition-colors duration-300 ${onDark ? "text-cream/80 hover:text-cream" : "text-ink/80 hover:text-ink"}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/enquire"
            data-testid="nav-quote-button"
            className={`hidden md:inline-flex items-center gap-2 text-sm tracking-wide px-6 py-3 transition-colors duration-400 group ${
              onDark ? "bg-gold text-ink hover:bg-gold-hover" : "bg-ink text-cream hover:bg-gold-brass"
            }`}
          >
            Request a Quote
            <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen(!open)}
            className={`md:hidden p-2 transition-colors duration-500 ${onDark ? "text-cream" : "text-ink"}`}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-cream/95 backdrop-blur-xl border-b border-gold/15"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <Link
                  key={l.testid}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className="font-serif text-2xl text-ink"
                >
                  {l.label}
                </Link>
              ))}
              <Link
                to="/enquire"
                onClick={() => setOpen(false)}
                className="inline-flex items-center justify-center gap-2 bg-ink text-cream text-sm tracking-wide px-6 py-4 mt-2"
              >
                Request a Quote <ArrowUpRight size={15} />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
